import { Err, isOk, Ok, Result } from "@pacote/result"
import useSWRMutation from "swr/mutation"
import { z } from "zod"

import { baseApiErrorResponseSchema } from "./api-config"
import { withAuthClientFetcher } from "./base-fetchers"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/
type VerifyOtpPayload = {
  contacts: Partial<Record<"phonePrefix" | "phoneNumber" | "email", string>>
  otp: string
  isVerify: boolean
  jwt: string
}

const verifyOtpServiceErrorResponseSchema = baseApiErrorResponseSchema.extend({
  counterCheck: z.number().optional(),
  counterRefresh: z.number().optional(),
})

type VerifyOtpErrorResponse = z.infer<
  typeof verifyOtpServiceErrorResponseSchema
>

type VerifyOtpFetcherReturn = Result<
  null,
  { type: "ERROR_MESSAGE" | "UNKNOWN"; errorMessage?: string }
>

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/
const verifyOtpServiceEndpoint = "/otp-security-ms/ms/contact/check"
/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/
const verifyOtpFetcher = async (
  key: string,
  {
    arg: {
      contacts: { email, phoneNumber, phonePrefix },
      ...rest
    },
  }: { arg: VerifyOtpPayload },
): Promise<VerifyOtpFetcherReturn> => {
  const responseResult = await withAuthClientFetcher<
    void,
    VerifyOtpErrorResponse
  >({
    url: key,
    method: "POST",
    body: {
      ...rest,
      ...(phonePrefix ? { phonePrefix } : {}),
      ...(phoneNumber ? { phoneNumber } : {}),
      ...(email ? { email } : {}),
    },
    errorSchemaValidator: verifyOtpServiceErrorResponseSchema,
  })

  if (isOk(responseResult)) return Ok(null)

  const response = responseResult.value

  if (response.status !== 422) return Err({ type: "UNKNOWN" })

  const errorInfos = response.data.error?.errorInfos || {}
  const counterCheck = response.data.counterCheck
  const counterRefresh = response.data.counterRefresh

  const errorKey = Object.keys(errorInfos)[0] as keyof typeof errorInfos

  if (!errorInfos[errorKey] || !errorInfos[errorKey][0])
    return Err({ type: "UNKNOWN" })

  let errorMessage = errorInfos[errorKey][0]

  if (counterCheck && counterCheck <= 2) errorMessage = "Otp.invalidOtpCode"
  if (counterCheck && counterCheck >= 3) {
    errorMessage = "Otp.generateNewCode"
    if (counterRefresh && counterRefresh >= 3)
      errorMessage = "Otp.expiredOtpCode"
  }

  return Err({
    type: "ERROR_MESSAGE",
    errorMessage,
  })
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

const useVerifyOtpMutation = () =>
  useSWRMutation(verifyOtpServiceEndpoint, verifyOtpFetcher)

export { useVerifyOtpMutation }
